import { type PriceItemType } from 'core/accounting/domain/PriceItemType'
import {
  mapApiToPriceItemType,
  type RawPriceItemType,
} from 'core/accounting/infra/PriceItemTypeMapper'
import { mapApiToAppointment, type RawAppointment } from 'core/appointments/infra/appointmentMapper'
import { type RawContainer } from 'core/container-details/domain/ShipmentContainer'
import { mapApiToContainer } from 'core/container-details/infra/ContainerMapper'
import { type Currency } from 'core/domain/Currency'
import { type EntityType } from 'core/domain/EntityType'
import { type EquipmentSubtypes } from 'core/domain/Load'
import { type RawManagedBy } from 'core/domain/ManagedBy'
import { type FullMode } from 'core/domain/Mode'
import { type Stop, type StopType } from 'core/domain/Stop'
import { type TruckType } from 'core/domain/Truck'
import { type RawLoadMeasures } from 'core/infra/LoadMeasuresMapper'
import { mapApiToManagedBy } from 'core/infra/ManagedByMapper.ts'
import { type PackageType } from 'core/shipping-items/domain/PackageType'
import { type RawItem } from 'core/shipping-items/domain/RawShippingItems'
import { type ShippingItem } from 'core/shipping-items/domain/ShippingItem'
import {
  mapApiToShippingItems,
  mapApiToShippingMeasures,
} from 'core/shipping-items/infra/ShippingItemsMapper'
import { createDate } from 'core/utils/date'

import { type Quote } from '../domain/Quote'
import { type QuoteAccessorial } from '../domain/QuoteAccessorial'
import { type QuoteEquipmentRequirements } from '../domain/QuoteEquipmentRequirements'
import { type QuoteStatus } from '../domain/QuoteStatus'
import { type RateStatus, type Rate } from '../domain/Rate'
import { type RateLine } from '../domain/RateLine'
import { type Rates } from '../domain/Rates'

export type BulkRateAvailability = {
  isBidAvailable: boolean
  reason?: string
}

export type RawQuoteEquipmentRequirements = {
  with_tarp: boolean
  tarp_size: string
  tarp_type: string
  dunnage: boolean
  equipment_subtypes?: Array<EquipmentSubtypes>
  temperature?: string
  temperature_unit?: string
}

export type RawQuoteAccessorial = {
  name: string
  code: string
  stop_type: Array<StopType>
  modes: Array<FullMode>
}

export type RawRateLine = {
  quantity: string
  subtype?: string
  total: string
  type: {
    value: string
    label: string
  }
  unit_price: string
  uuid: string
  notes?: string
  price_item_type?: Omit<RawPriceItemType, 'stops_relationship'>
  stop_type?: StopType
}

export type RawCarrier = {
  id: number
  uuid: string
  name: string | null
  email: string | null
  dot: string | null
  scac: string | null
  mc: string | null
  entity_type?: EntityType
}

export type RawShipper = {
  id: number
  email: string
  logo_email_url: string | null
  name: string | null
  phone_number: string | null
  company_name: string
  is_test: boolean
}

type RawBulkRateAvailability = {
  is_bid_available: boolean
  reason: string | null
}

export type RawQuoteStop = {
  address: string | null
  city: string
  country: string
  date: string | null
  state: string
  zipcode: string | null
  full_street_address?: string | null
  appointment?: RawAppointment
}

export type RawCommodity = {
  description: string
  freight_class: string
  hazmat: boolean
  hazmat_class?: string
  hazmat_packing_group?: string
  hazmat_proper_shipping_name?: string
  nmfc_code: string
  package_count: number
  package_type: string
  un_number: string
  uuid: string
  weight: number | string
  weight_type: string
}

export type RawQuoteMeasures = {
  total_weight: number | string
  total_volume: number | string
  density?: number | string
  total_linear_feet: number | string
}

export type RawQuoteItem = {
  commodity?: string
  weight?: string | number
  package_type?: PackageType
  package_count?: number
  width?: string | number
  length?: string | number
  height?: string | number
  stackable?: boolean
  turnable?: boolean
  freight_class?: string
  nmfc_code?: string
  commodities: Array<RawCommodity>
}

export type RawQuote = {
  bids_deadline: string
  can_receive_proposals: boolean
  equipment_type: TruckType
  equipment_requirements?: RawQuoteEquipmentRequirements
  items: Array<RawItem>
  mileage: number | null
  mode: FullMode
  notes?: string | null
  shipper_guide_id: string
  shipper_notes: string | null
  stops: Array<RawQuoteStop>
  accessorials: Array<RawQuoteAccessorial>
  requested_accessorials?: Array<RawPriceItemType>
  measures: RawLoadMeasures | null
  power_only?: boolean
  target_rate?: string
  managed_by: RawManagedBy
  currency: Currency
  conversion_rate: string | null
  status: QuoteStatus
  container?: RawContainer
}

export type RawRate = {
  uuid: string
  status: RateStatus
  flat_rate: string
  per_mile_rate: number
  carrier_notes: string | null
  availability: RawBulkRateAvailability
  notes: string | null
  quote: RawQuote
  ratelines: Array<RawRateLine>
  book_now_enabled?: boolean
  shipment_cargo_value: number | null
  shipment?: {
    uuid: string
    carrier_1xp_link: string
  }
  submitted_at: string
  created_at: string
  under_negotiation: boolean
  negotiation_note: string | null
}

export type RawRates = {
  count: number
  next: string
  previous: string
  shipper: RawShipper
  carrier_name: string
  carrier_email: string
  carrier: RawCarrier
  all_quotes_expired: boolean
  all_rates_sent: boolean
  accessorials_history: Array<any>
  results: Array<RawRate>
}

function handleFalsyValues<T>(value: T) {
  return value ?? undefined
}

function mapApiToShipper(rawShipper: RawShipper) {
  return {
    id: rawShipper.id,
    email: rawShipper.email,
    logoUrl: handleFalsyValues(rawShipper.logo_email_url),
    name: handleFalsyValues(rawShipper.name),
    phoneNumber: handleFalsyValues(rawShipper.phone_number),
    companyName: rawShipper.company_name,
    isTest: rawShipper.is_test,
  }
}

function mapApiToCarrier(rawCarrier: RawCarrier) {
  return {
    id: rawCarrier.id,
    uuid: rawCarrier.uuid,
    name: handleFalsyValues(rawCarrier.name),
    email: handleFalsyValues(rawCarrier.email),
    entityType: rawCarrier.entity_type,
    dot: handleFalsyValues(rawCarrier.dot),
    mc: handleFalsyValues(rawCarrier.mc),
    scac: handleFalsyValues(rawCarrier.scac),
  }
}

function mapApiToBulkRateAvailability(
  rawBulkRateAvailability: RawBulkRateAvailability
): BulkRateAvailability {
  return {
    isBidAvailable: rawBulkRateAvailability.is_bid_available,
    reason: rawBulkRateAvailability.reason ?? undefined,
  }
}

export function mapApiToRateLine(rawRateLine: RawRateLine): RateLine {
  return {
    quantity: Number(rawRateLine.quantity),
    subtype: rawRateLine.subtype,
    total: Number(rawRateLine.total),
    type: {
      value: rawRateLine.type.value,
      label: rawRateLine.type.label,
    },
    unitPrice: Number(rawRateLine.unit_price),
    notes: rawRateLine.notes,
    priceItemType: rawRateLine.price_item_type
      ? {
          uuid: rawRateLine.price_item_type.uuid,
          label: rawRateLine.price_item_type.label,
        }
      : undefined,
    stopType: rawRateLine.stop_type,
  }
}

function mapApiToStop(quoteStop: RawQuoteStop): Stop {
  return {
    address: quoteStop.address ?? undefined,
    city: quoteStop.city,
    country: quoteStop.country,
    date: quoteStop.date ?? undefined,
    state: quoteStop.state,
    zipCode: quoteStop.zipcode ?? undefined,
    fullStreetAddress: quoteStop.full_street_address ?? undefined,
    appointment: mapApiToAppointment(quoteStop.appointment),
  }
}

function mapApiToAccessorial(quoteAccessorial: RawQuoteAccessorial): QuoteAccessorial {
  return {
    name: quoteAccessorial.name,
    code: quoteAccessorial.code,
    stopType: quoteAccessorial.stop_type,
  }
}

function mapEquipmentRequirements(
  equipmentRequirements: RawQuoteEquipmentRequirements
): QuoteEquipmentRequirements {
  const {
    with_tarp,
    tarp_type,
    tarp_size,
    dunnage,
    equipment_subtypes,
    temperature,
    temperature_unit,
  } = equipmentRequirements

  return {
    withTarp: with_tarp,
    tarpType: tarp_type,
    tarpSize: tarp_size,
    dunnage: dunnage,
    equipmentSubtypes: equipment_subtypes?.[0],
    temperature: temperature,
    temperatureUnit: temperature_unit,
  }
}

export function mapApiToQuote(quote: RawQuote): Quote {
  const items: Array<ShippingItem> = mapApiToShippingItems(quote.items)
  const stops: Array<Stop> = (quote.stops ?? []).map(mapApiToStop)
  const accessorials: Array<QuoteAccessorial> = (quote.accessorials ?? []).map(mapApiToAccessorial)
  const requestedAccessorials: Array<PriceItemType> = (quote.requested_accessorials ?? []).map(
    mapApiToPriceItemType
  )
  const equipmentRequirements = quote?.equipment_requirements
    ? mapEquipmentRequirements(quote?.equipment_requirements)
    : undefined

  return {
    items,
    stops,
    bidsDeadline: createDate(quote.bids_deadline)!,
    canReceiveProposals: quote.can_receive_proposals,
    equipmentType: quote.equipment_type,
    equipmentRequirements,
    mileage: quote.mileage ?? undefined,
    measures: mapApiToShippingMeasures(quote.measures),
    mode: quote.mode,
    notes: quote.notes ?? undefined,
    shipperGuideId: quote.shipper_guide_id,
    shipperNotes: quote.shipper_notes ?? undefined,
    accessorials,
    requestedAccessorials,
    powerOnly: quote.power_only,
    targetRate: quote.target_rate,
    managedBy: mapApiToManagedBy(quote.managed_by),
    conversionRate: handleFalsyValues(quote.conversion_rate),
    currency: quote.currency,
    status: quote.status,
    container: mapApiToContainer(quote.container),
  }
}

function mapShipment(rawShipment?: { uuid: string; carrier_1xp_link: string }) {
  if (!rawShipment) return undefined

  return {
    uuid: rawShipment.uuid,
    shipmentDetailsLink: rawShipment.carrier_1xp_link,
  }
}

export function mapApiToRate(rawRate: RawRate): Rate {
  return {
    uuid: rawRate.uuid,
    status: rawRate.status,
    flatRate: rawRate.flat_rate,
    perMileRate: rawRate.per_mile_rate,
    carrierNotes: rawRate.carrier_notes,
    availability: mapApiToBulkRateAvailability(rawRate.availability),
    bookNowEnabled: Boolean(rawRate.book_now_enabled),
    cargoValue: rawRate.shipment_cargo_value ?? undefined,
    ratelines: (rawRate.ratelines ?? []).map(mapApiToRateLine),
    submittedAt: new Date(rawRate.submitted_at),
    createdAt: new Date(rawRate.created_at),
    shipment: mapShipment(rawRate.shipment),
    notes: rawRate.notes,
    quote: mapApiToQuote(rawRate.quote),
    underNegotiation: rawRate.under_negotiation,
    negotiationNote: rawRate.negotiation_note,
  }
}

export function mapApiToRates(rawRates: RawRates): Rates {
  return {
    count: rawRates.count,
    next: rawRates.next,
    previous: rawRates.previous,
    shipper: mapApiToShipper(rawRates.shipper),
    carrier: mapApiToCarrier(rawRates.carrier),
    allQuotesExpired: rawRates.all_quotes_expired,
    allRatesSent: rawRates.all_rates_sent,
    accessorialsHistory: rawRates.accessorials_history,
    results: rawRates.results.map(mapApiToRate),
  }
}

export function mapApiToRefreshTokenData({ email_recipient }: { email_recipient?: string }) {
  return handleFalsyValues(email_recipient)
}
